// src/pages/user/AccountSettingsHeader.jsx
import React, { useState, useEffect, useRef } from "react";
import TransactionPinModal from "./TransactionPinModal";
import SecuritySettingsModal from "./SecuritySettingsModal";
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Stack,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SecurityIcon from "@mui/icons-material/Security";
import VpnKeyIcon from "@mui/icons-material/VpnKey";

import { getUser, uploadAvatar } from "../../utils/api";

const AccountSettingsHeader = ({ avatarUrl, setAvatarUrl }) => {
  const [user, setUser] = useState(null);
  const [preview, setPreview] = useState(null);
  const [transactionOpen, setTransactionOpen] = useState(false);
  const [securityOpen, setSecurityOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const isMounted = useRef(true);

  // ✅ Load user on mount
  useEffect(() => {
    isMounted.current = true;

    getUser()
      .then((u) => {
        if (isMounted.current) {
          setUser(u);

          // ✅ trust backend full URL if available
          if (!avatarUrl && u.avatar_url) {
            setAvatarUrl(u.avatar_url);
          }
        }
      })
      .catch((err) => console.error("Failed to load user:", err));

    return () => {
      isMounted.current = false;
      if (preview) URL.revokeObjectURL(preview);
    };
  }, []);

  // ✅ Avatar upload
  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const tempPreview = URL.createObjectURL(file);
    setPreview(tempPreview);

    try {
      const res = await uploadAvatar(file);
      const updatedUser = res.data.user;

      const avatarUrl = updatedUser.avatar_url || updatedUser.avatar;
      if (!avatarUrl) {
        throw new Error("Server did not return a valid avatar URL.");
      }

      // ✅ force refresh with timestamp
      const freshUrl = avatarUrl + "?t=" + Date.now();
      setAvatarUrl(freshUrl);
      setUser(updatedUser);

      setSnackbar({
        open: true,
        message: "Profile Picture updated successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Avatar upload/display failed:", err);

      let message = "Failed to upload avatar.";
      if (err.response?.data?.errors?.avatar?.length) {
        message = err.response.data.errors.avatar[0];
      }

      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      URL.revokeObjectURL(tempPreview);
      setPreview(null);
    }
  };

  // ✅ initials fallback
  const initials = user?.name
    ? user.name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
    : "??";

  const handleCloseSnackbar = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  // ✅ priority: preview → parent avatarUrl → backend avatar_url
  const displayAvatar = avatarUrl ? `${avatarUrl}?t=${Date.now()}` : null;

  return (
    <Box
      sx={{
        mt: 6,
        mr: { xs: 2, md: 4 },
        mb: 3,
      }}
    >
      {/* Breadcrumb */}
      <Typography
        variant="body2"
        fontSize={12.5}
        color="text.secondary"
        mb={1}
        align="left"
      >
        Dashboard &gt; Settings
      </Typography>

      {/* Title */}
      <Typography variant="h6" fontWeight={600} mb={2}>
        Account Settings
      </Typography>

      {/* User Card */}
      <Paper
        elevation={3}
        sx={{
          borderRadius: 3,
          background: "linear-gradient(to bottom, #3AB7F9, #0164C1)",
          color: "#fff",
          px: 2,
          pt: 3,
          pb: 2,
          mr: 2,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box position="relative" display="inline-block" mb={1.5}>
          <Avatar
            src={displayAvatar}
            sx={{
              width: 70,
              height: 70,
              fontSize: 26,
              bgcolor: "#d1fae5",
              color: "#000",
              border: "4px solid #fff",
              mx: "auto",
              fontWeight: 700,
            }}
          >
            {!displayAvatar && initials}
          </Avatar>

          {/* Upload button */}
          <input
            type="file"
            accept="image/*"
            id="avatar-upload"
            style={{ display: "none" }}
            onChange={handleAvatarChange}
          />
          <label htmlFor="avatar-upload">
            <IconButton
              component="span"
              sx={{
                position: "absolute",
                bottom: -4,
                right: -4,
                bgcolor: "#fff",
                color: "#0ea5e9",
                width: 28,
                height: 28,
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { bgcolor: "#f0f0f0" },
              }}
            >
              <CameraAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </label>
        </Box>

        {/* User Info */}
        <Typography fontWeight="bold" fontSize="1.1rem">
          {user?.name || "Loading..."}
        </Typography>
        <Typography fontSize="0.85rem" sx={{ opacity: 0.9 }}>
          Account #{user?.account_number || "--------"}
        </Typography>

        {/* Decorative Waves */}
        <Box
          component="img"
          src="/waves.svg"
          alt=""
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            pointerEvents: "none",
            opacity: 0.4,
          }}
        />
      </Paper>

      {/* Action Buttons */}
      <Paper elevation={1} sx={{ mr: 2, mt: 1, borderRadius: 3, px: 1.5, py: 2 }}>
        <Stack spacing={1.5}>
          <Button
            startIcon={<PersonOutlineIcon />}
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "#e0f2fe",
              color: "#0284c7",
              fontWeight: 500,
              height: "3.5rem",
              justifyContent: "flex-start",
              textTransform: "none",
              "&:hover": { backgroundColor: "#bae6fd" },
            }}
          >
            Profile Information
          </Button>

          <Button
            startIcon={<SecurityIcon />}
            fullWidth
            onClick={() => setSecurityOpen(true)}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              color: "#1e293b",
              fontWeight: 500,
            }}
          >
            Security Settings
          </Button>

          <Button
            startIcon={<VpnKeyIcon />}
            fullWidth
            onClick={() => setTransactionOpen(true)}
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              color: "#1e293b",
              fontWeight: 500,
            }}
          >
            Transaction PIN
          </Button>
        </Stack>
      </Paper>

      {/* Modals */}
      <TransactionPinModal
        open={transactionOpen}
        handleClose={() => setTransactionOpen(false)}
      />
      <SecuritySettingsModal
        open={securityOpen}
        handleClose={() => setSecurityOpen(false)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountSettingsHeader;